import { Link, createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetTournamentQueryKey,
  getListMyTournamentEntriesQueryKey,
  getListTournamentsQueryKey,
  useEnterTournament,
  useListMyTournamentEntries,
  useListTournaments,
  useWithdrawFromTournament,
} from '../api/generated/endpoints/tournaments/tournaments'
import type { MyEntryRead, TournamentSummary } from '../api/generated/model'
import { formatInstant } from '../lib/time'
import { apiMessage } from '../lib/api-error'
import { useSession } from '../lib/auth'
import { formatUYU } from '../lib/money'
import { formatDayLong } from '../lib/time'
import { TOURNAMENT_STATUS_LABELS, entryState, whenLabel } from '../lib/tournaments'

/**
 * What's on, and a way in.
 *
 * Public, like the standings page — somebody who has not signed in should be
 * able to see that there is a tournament next month before deciding whether
 * this place is for them. Entering needs a login, and the button says so
 * rather than the page hiding itself.
 */
export const Route = createFileRoute('/tournaments')({
  component: TournamentsPage,
})

function TournamentsPage() {
  const session = useSession()
  const tournaments = useListTournaments()
  const mine = useListMyTournamentEntries({ query: { enabled: Boolean(session) } })

  if (tournaments.isPending) return <p className="text-slate-600">Cargando…</p>
  if (tournaments.isError) {
    return (
      <p role="alert" className="text-red-600">
        No se pudieron cargar los torneos.
      </p>
    )
  }

  // By slug, so the card can show the round this person holds rather than
  // only whether they are on the list.
  const myEntries = new Map(
    (mine.data?.entries ?? []).map((entry) => [entry.tournament_slug, entry]),
  )
  const list = tournaments.data.tournaments

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Torneos</h1>
        <p className="mt-1 text-slate-600">
          Torneos mensuales de GSPro. Todos juegan la misma cancha con hándicap, y la tabla de
          posiciones es un enlace que cualquiera puede abrir.
        </p>
      </div>

      {list.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-slate-600">
          Todavía no hay nada agendado. Vale la pena volver a mirar pronto.
        </p>
      ) : (
        <ul className="space-y-4">
          {list.map((tournament) => (
            <TournamentCard
              key={tournament.slug}
              tournament={tournament}
              signedIn={Boolean(session)}
              entry={myEntries.get(tournament.slug) ?? null}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function TournamentCard({
  tournament,
  signedIn,
  entry,
}: {
  tournament: TournamentSummary
  signedIn: boolean
  entry: MyEntryRead | null
}) {
  const entered = entry !== null
  const queryClient = useQueryClient()

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListTournamentsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListMyTournamentEntriesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getGetTournamentQueryKey(tournament.slug) }),
    ])
  }

  const enter = useEnterTournament({
    mutation: {
      retry: false, // never auto-retry something that might take money
      onSuccess: async (result) => {
        await refresh()
        // Only when there is something to pay. With payments switched off the
        // entry is already done and there is nowhere to send them.
        if (result.checkout_url) window.location.href = result.checkout_url
      },
    },
  })

  const withdraw = useWithdrawFromTournament({
    mutation: { retry: false, onSuccess: refresh },
  })

  const state = entryState(tournament)
  const busy = enter.isPending || withdraw.isPending

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">
          <Link
            to="/t/$slug"
            params={{ slug: tournament.slug }}
            className="inline-flex min-h-11 items-center hover:underline"
          >
            {tournament.name}
          </Link>
        </h2>
        <span className="text-sm text-slate-500">
          {TOURNAMENT_STATUS_LABELS[tournament.status] ?? tournament.status}
        </span>
      </div>

      <p className="mt-1 text-slate-600">
        {whenLabel(
          tournament.event_date,
          tournament.plays_from,
          tournament.plays_until,
          formatDayLong,
        )}
        {tournament.gspro_course && <> &middot; {tournament.gspro_course}</>}
      </p>

      <p className="mt-1 text-sm text-slate-500">
        {tournament.entry_fee_uyu_pesos > 0
          ? formatUYU(tournament.entry_fee_uyu_pesos)
          : 'Inscripción libre'}
        {' · '}
        {tournament.entrants} {tournament.entrants === 1 ? 'inscripto' : 'inscriptos'}
        {tournament.spots_left !== null && <> · quedan {tournament.spots_left}</>}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Link
          to="/t/$slug"
          params={{ slug: tournament.slug }}
          className="inline-flex min-h-12 items-center rounded-md border border-slate-300 px-4 hover:bg-slate-50"
        >
          Posiciones
        </Link>

        {entered ? (
          <>
            <span className="text-sm font-medium text-green-700">Inscripto</span>
            {/* Signing up and choosing when to play are two steps on purpose:
                somebody can take their place in a capped field now and work out
                their week later. Which is also why an entry with no round is an
                ordinary state rather than an unfinished one. */}
            {entry?.round ? (
              <Link
                to="/round/$slug"
                params={{ slug: tournament.slug }}
                className="inline-flex min-h-12 items-center rounded-md border border-slate-300 px-4 hover:bg-slate-50"
              >
                {formatInstant(entry.round.starts_at)} &middot; {entry.round.bay_name}
              </Link>
            ) : (
              <Link
                to="/round/$slug"
                params={{ slug: tournament.slug }}
                className="inline-flex min-h-12 items-center rounded-md bg-slate-900 px-5 text-white hover:bg-slate-700"
              >
                Elegir horario
              </Link>
            )}
            {state === 'open' && (
              <button
                type="button"
                onClick={() => withdraw.mutate({ slug: tournament.slug })}
                disabled={busy}
                className="min-h-12 rounded-md px-3 text-sm text-slate-600 underline hover:text-slate-900 disabled:opacity-50"
              >
                Darse de baja
              </button>
            )}
          </>
        ) : state === 'open' ? (
          signedIn ? (
            <button
              type="button"
              onClick={() => enter.mutate({ slug: tournament.slug })}
              disabled={busy}
              className="min-h-12 rounded-md bg-slate-900 px-5 text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {enter.isPending ? 'Un momento…' : 'Inscribirse'}
            </button>
          ) : (
            <Link
              to="/login"
              search={{ redirect: '/tournaments' }}
              className="inline-flex min-h-12 items-center rounded-md bg-slate-900 px-5 text-white hover:bg-slate-700"
            >
              Iniciar sesión para inscribirse
            </Link>
          )
        ) : (
          <span className="text-sm text-slate-500">
            {state === 'full' ? 'Completo' : 'Inscripciones cerradas'}
          </span>
        )}
      </div>

      {(enter.isError || withdraw.isError) && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {apiMessage(enter.error ?? withdraw.error, 'No funcionó. Intentar de nuevo.')}
        </p>
      )}
    </li>
  )
}
